import React from 'react';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import styled, { css } from 'styled-components';
import { useGlobalUser } from '@sparknz/journeys-common';
import {
  Box,
  ButtonVariant,
  ProductCardSection,
} from '@sparknz/spark-pattern-library';

import { isPack } from 'components/shop/MobilePlans/utils/isPack.utils';
import { useCategorySelection } from 'components/shop/shared/providers';
import {
  MobilePlanDetails,
  OfferEligibilityLevelEnum,
} from '../../typings/plans';
import ChangePlanButton from './ChangePlanButton';
import { CheckEligibilityButton } from './CheckEligibilityButton';
import MpdPlanCardSelect, {
  StyledProductCardButton,
} from './MpdPlanCardSelect';

interface PlanCardActionProps {
  href?: string | null;
  label: string;
  planCardProduct: MobilePlanDetails;
  ctaType: string;
  variant: ButtonVariant;
  isMultiPlanEnabled: boolean;
}

function PlanCardAction({
  href,
  label,
  planCardProduct: { planDetails, productCharacteristics },
  ctaType,
  variant,
  isMultiPlanEnabled,
}: PlanCardActionProps) {
  const {
    planId: planOfferId,
    planName,
    isTeamUpEligible,
    categoryIdentifiers,
  } = planDetails;

  const { offerEligibilityLevel, relatedOfferId, relatedOfferIds } =
    productCharacteristics || {};

  const {
    authenticatedInfo: { isLoggedIn, hasSparkId },
  } = useGlobalUser();

  const isEligibilityCheckRequired =
    offerEligibilityLevel === OfferEligibilityLevelEnum.Account;

  if (isEligibilityCheckRequired && ctaType === 'ADD_TO_CART') {
    return (
      <CheckEligibilityButton
        categoryIdentifiers={categoryIdentifiers}
        planId={planOfferId}
        planName={planName}
        offerEligibilityLevel={offerEligibilityLevel}
        relatedOfferId={relatedOfferId}
        relatedOfferIds={relatedOfferIds}
        label={
          hasSparkId ? 'Check eligibility' : 'Sign in to check eligibility'
        }
      />
    );
  }

  if (ctaType === 'ADD_TO_CART') {
    return (
      <MpdPlanCardSelect
        planId={planOfferId}
        planName={planName}
        relatedOfferId={relatedOfferId}
        relatedOfferIds={relatedOfferIds}
        isTeamUpEligible={!!isTeamUpEligible}
        isMultiPlanEnabled={isMultiPlanEnabled}
      />
    );
  }

  if (ctaType === 'CHANGE_PLAN') {
    const changePlanLabel =
      !isLoggedIn || !hasSparkId ? 'Sign in to change plan' : 'Change Plan';

    return (
      <ChangePlanButton
        label={changePlanLabel}
        planId={planOfferId}
        variant={variant}
        isEligibilityCheckRequired={isEligibilityCheckRequired}
      />
    );
  }

  if (ctaType === 'LINK') {
    return (
      <StyledProductCardButton variant={variant} component="a" href={href}>
        {label}
      </StyledProductCardButton>
    );
  }

  return null;
}

type ActionsContainerProps = {
  isMultiCta: boolean;
};

const ActionsContainer = styled(Box)<ActionsContainerProps>`
  ${({ theme: { media }, isMultiCta }) => css`
    ${media.up('slim')} {
      // @TODO: This is not a final and perfect solution but required to align the CTAs properly for release 22.10
      height: ${isMultiCta ? `112px` : `auto`};
    }
  `}
`;

type MobilePlanCardActionsSectionProps = {
  ctaOverrides: MobilePlanDetails['ctaOverrides'];
  hasMultiCtas: boolean;
  planCardProduct: MobilePlanDetails;
  isMultiPlanEnabled: boolean;
};

export default function MobilePlanCardActionsSection({
  ctaOverrides,
  hasMultiCtas,
  planCardProduct,
  isMultiPlanEnabled,
}: MobilePlanCardActionsSectionProps) {
  const sortedCtas = sortBy(ctaOverrides, ['sortOrder']);

  const showPackLabel = isPack(planCardProduct.planDetails.planName);

  const { selectedCategoryId } = useCategorySelection();

  const cardButtonContent =
    selectedCategoryId === 'change_plan' ? 'CTA' : 'Select plan';

  const planCartAction =
    selectedCategoryId === 'change_plan' ? 'CHANGE_PLAN' : 'ADD_TO_CART';

  const planCardActionLabel = showPackLabel ? 'Select pack' : cardButtonContent;

  return (
    <ProductCardSection>
      <ActionsContainer
        isMultiCta={hasMultiCtas}
        flexDirection="column"
        gap="s2"
      >
        {sortedCtas.map(({ data, label, type: ctaType, style }) => {
          if (!label || !ctaType) {
            return null;
          }
          return (
            <Box flexDirection="row" alignItems="center" key={label}>
              <PlanCardAction
                href={data}
                label={label}
                ctaType={ctaType}
                planCardProduct={planCardProduct}
                variant={
                  style?.toLowerCase() === 'secondary' ? 'default' : 'primary'
                }
                isMultiPlanEnabled={isMultiPlanEnabled}
              />
            </Box>
          );
        })}
        {isEmpty(sortedCtas) ? (
          <PlanCardAction
            label={planCardActionLabel}
            planCardProduct={planCardProduct}
            ctaType={planCartAction}
            variant="primary"
            isMultiPlanEnabled={isMultiPlanEnabled}
          />
        ) : null}
      </ActionsContainer>
    </ProductCardSection>
  );
}
